import React, { useEffect } from "react";
import { Table } from "antd";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const currency = localStorage.getItem("currencycode") || "₹";

const WalkInVsOta = ({ rooms, applicableTaxes, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle }) => {
  const { t } = useTranslation();

  // Group data by category (Walk-in, OTA)
  const groupedData = () => {
    const grouped = {
      "Walk-in": {
        key: "Walk-in",
        bookings: 0,
        nights: 0,
        revenue: 0,
        discount: 0,
        net_revenue: 0,
        ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
      },
      OTA: {
        key: "OTA",
        bookings: 0,
        nights: 0,
        revenue: 0,
        discount: 0,
        net_revenue: 0,
        ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
      },
    };

    const otaSources = ["Booking.com", "Expedia", "Agoda"];
    rooms.forEach((room) => {
      const category = room.reservation_type === "Walk-in" ? "Walk-in" : otaSources.includes(room.booking_source) ? "OTA" : null;
      if (category) {
        grouped[category].bookings += 1;
        grouped[category].nights += room.nights;
        grouped[category].revenue += room.total_revenue;
        grouped[category].discount += room.discount;
        grouped[category].net_revenue += room.total_revenue - room.discount;
        applicableTaxes.forEach((taxName) => {
          grouped[category][taxName] += room[taxName] || 0;
        });
      }
    });

    return Object.values(grouped).filter((item) => item.bookings > 0);
  };

  // Sorting logic
  const sortedData = () => {
    return [...groupedData()].sort((a, b) => a.key.localeCompare(b.key));
  };

  // Totals
  const totals = groupedData().reduce(
    (acc, item) => ({
      bookings: acc.bookings + item.bookings,
      nights: acc.nights + item.nights,
      revenue: acc.revenue + item.revenue,
      discount: acc.discount + item.discount,
      net_revenue: acc.net_revenue + item.net_revenue,
      ...applicableTaxes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + item[taxName],
      }), acc),
    }),
    {
      bookings: 0,
      nights: 0,
      revenue: 0,
      discount: 0,
      net_revenue: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  // Chart data
  const barChartData = {
    labels: ["Walk-in", "OTA"],
    datasets: [
      {
        label: t("revenue"),
        data: [
          groupedData().find((item) => item.key === "Walk-in")?.revenue || 0,
          groupedData().find((item) => item.key === "OTA")?.revenue || 0,
        ],
        backgroundColor: ["#28a745", "#007bff"],
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: t("revenue") } },
      x: { title: { display: true, text: t("category") } },
    },
  };

  // Export data and headers
  const exportHeaders = [
    { key: "key", label: t("category") },
    { key: "bookings", label: t("total_bookings") },
    { key: "nights", label: t("nights") },
    { key: "revenue", label: `${t("revenue")} (${currency})` },
    { key: "discount", label: `${t("commission_discount")} (${currency})` },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      label: `${taxName} (${currency})`,
    })),
    { key: "net_revenue", label: `${t("net_revenue")} (${currency})` },
  ];

  const exportData = groupedData().map((item) => ({
    key: item.key,
    bookings: item.bookings,
    nights: item.nights,
    revenue: item.revenue.toFixed(2),
    discount: item.discount.toFixed(2),
    ...applicableTaxes.reduce((acc, taxName) => ({
      ...acc,
      [taxName]: item[taxName].toFixed(2),
    }), {}),
    net_revenue: item.net_revenue.toFixed(2),
  }));

  const totalsForPageFooter = [
    { key: t("total_bookings"), value: totals.bookings },
  ];

  const exportFooter = [
    { key: "bookings", value: totals.bookings, currency: false },
    { key: "nights", value: totals.nights, currency: false },
    { key: "revenue", value: totals.revenue, currency: true },
    { key: "discount", value: totals.discount, currency: true },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      value: totals[taxName],
      currency: true,
    })),
    { key: "net_revenue", value: totals.net_revenue, currency: true },
  ];

  // Update parent export states
  useEffect(() => {
    setExportData(exportData);
    setExportHeaders(exportHeaders);
    setExportFooter(exportFooter);
    setTotalsForPageFooter(totalsForPageFooter);
    setTotalColumns(["bookings", "nights", "revenue", "discount", ...applicableTaxes, "net_revenue"]);
    setReportTitle(t("walk_in_vs_ota"));
  }, [rooms, applicableTaxes, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle, t, exportData, exportHeaders, exportFooter, totalsForPageFooter]);

  const columns = [
    {
      title: t("category"),
      dataIndex: "key",
      key: "key",
      sorter: (a, b) => a.key.localeCompare(b.key),
    },
    {
      title: t("total_bookings"),
      dataIndex: "bookings",
      key: "bookings",
      sorter: (a, b) => a.bookings - b.bookings,
    },
    {
      title: t("nights"),
      dataIndex: "nights",
      key: "nights",
      sorter: (a, b) => a.nights - b.nights,
    },
    {
      title: t("revenue"),
      dataIndex: "revenue",
      key: "revenue",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: t("commission_discount"),
      dataIndex: "discount",
      key: "discount",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.discount - b.discount,
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a[taxName] - b[taxName],
    })),
    {
      title: t("net_revenue"),
      dataIndex: "net_revenue",
      key: "net_revenue",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.net_revenue - b.net_revenue,
      onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
    },
  ];

  return (
    <div className="walk-in-vs-ota-container">
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("walk_in_vs_ota_revenue")}
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
            </div>
            <div className="card-body" style={{ padding: "10px", height: "300px" }}>
              <div style={{ height: "100%", width: "100%" }}>
                <Bar data={barChartData} options={barChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-0">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("walk_in_vs_ota")} (Total Bookings: {rooms.length})
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
            </div>
            <div className="card-body p-0">
              {rooms.length === 0 ? (
                <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                  <img
                    src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                    alt="No data available"
                    style={{ width: "150px", marginBottom: "20px" }}
                  />
                  <h3>{t("no_data")}</h3>
                  <p>{t("no_room_sold_data")}</p>
                </div>
              ) : (
                <Table
                  dataSource={sortedData()}
                  columns={columns}
                  rowKey="key"
                  pagination={false}
                  bordered
                  className="daybook-table"
                  style={{ margin: 0 }}
                  summary={() => (
                    <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                      <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                      <Table.Summary.Cell>{totals.bookings}</Table.Summary.Cell>
                      <Table.Summary.Cell>{totals.nights}</Table.Summary.Cell>
                      <Table.Summary.Cell>{currency}{totals.revenue.toFixed(2)}</Table.Summary.Cell>
                      <Table.Summary.Cell>{currency}{totals.discount.toFixed(2)}</Table.Summary.Cell>
                      {applicableTaxes.map((taxName, index) => (
                        <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                      ))}
                      <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{totals.net_revenue.toFixed(2)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .table th, .table td {
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .daybook-table .ant-table-thead > tr > th {
          background: #ffffff;
          font-weight: 600;
          padding: 8px 16px;
          font-size: 14px;
        }
        .daybook-table .ant-table-tbody > tr > td {
          padding: 12px 18px;
          font-size: 14px;
          height: 60px;
        }
        .daybook-table .ant-table {
          border: 1px solid #dee2e6;
        }
        .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
          background: #f5f5f5;
        }
        .chart-container {
          max-height: 300px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default WalkInVsOta;